import { MarkerType } from "reactflow";
import { convertMilliSecToReadable } from "../../utils/function";

const calculateTimeDifference = (sourceNodeTime, targetNodeTime) => {

  const sourcetimestamp_utc = new Date(sourceNodeTime.timestamp);
  const targettimestamp_utc = new Date(targetNodeTime.timestamp);
  const differenceInMs = Math.abs(targettimestamp_utc - sourcetimestamp_utc);

  if (isNaN(differenceInMs) || differenceInMs < 0) return 'Invalid Date Range';

  return convertMilliSecToReadable(differenceInMs);
};

const createStructureOverview = (nodes) => {
  const edges = [];

  for (var index = 1; index < nodes.length; index++) {
    const prevNode = nodes[index-1], currentNode = nodes[index];
    edges.push({
      id: `edgeStruct-${prevNode.id}-${currentNode.id}`,
      source: prevNode.id,
      target: currentNode.id,
      type: 'straight',
      style: { stroke: 'transparent', strokeWidth: 0 },
    });
  }

  return edges;
};

const createStructure = (nodes, stateGroups) => {
  const edges = [];
  const branchMapStruct = new Map();
  let lastNullBranchNodeStruct = null;

  for (const category in stateGroups) {
    nodes.forEach((node) => {
      stateGroups[category].forEach(stateCat => {
        const { repo, state } = node.data;

        if (state === stateCat) {
          if (repo === null) {
            if (branchMapStruct.size > 0) {
              // eslint-disable-next-line no-unused-vars
              for (const [repoKey, branchNode] of branchMapStruct.entries()) {
                edges.push({
                  id: `edgeStruct-${branchNode.id}-${node.id}`,
                  source: branchNode.id,
                  target: node.id,
                  type: 'straight',
                  style: { stroke: 'transparent', strokeWidth: 0 },
                });
              }
            }
            else if (lastNullBranchNodeStruct != null) {
              edges.push({
                id: `edgeStruct-${lastNullBranchNodeStruct.id}-${node.id}`,
                source: lastNullBranchNodeStruct.id,
                target: node.id,
                type: 'straight',
                style: { stroke: 'transparent', strokeWidth: 0 },
              });
            }
            lastNullBranchNodeStruct = node;
            branchMapStruct.clear();
          } else {
            if (!branchMapStruct.has(repo)) {
              if (lastNullBranchNodeStruct) {
                edges.push({
                  id: `edgeStruct-${lastNullBranchNodeStruct.id}-${node.id}`,
                  source: lastNullBranchNodeStruct.id,
                  target: node.id,
                  type: 'straight',
                  style: { stroke: 'transparent', strokeWidth: 0 },
                });
              }
            } else {
              const branchNode = branchMapStruct.get(repo);
              edges.push({
                id: `edgeStruct-${branchNode.id}-${node.id}`,
                source: branchNode.id,
                target: node.id,
                type: 'straight',
                style: { stroke: 'transparent', strokeWidth: 0 },
              });
            }
            branchMapStruct.set(repo, node);
          }
        }
      });
    });
  }

  return edges;
};

const createEdges = (nodes) => {
  const edges = [];
  var sequence = []; // array of array of source to target timestamp
  var branchMap = new Map();
  var branchOrder = new Map(); //track order of nodes per branch
  var categoryWiseReverseTransition = new Set();

  const nullBranchMap = new Map(); // first occurrence of states in the null branch
  const nullBranchOrder = []; // Order of nodes in the null branch
  let lastNullBranchNode = null;
  let lastNodeWithNullRepo = false;

  const edgeExists = (sourceId, targetId) => {
    return edges.some(edge => edge.source === sourceId && edge.target === targetId);
  };

  let tempLastNull = null;
  const tempProcessedStates = new Set();
  nodes.forEach((node) => {
    if (node.data.repo == null && !tempProcessedStates.has(node.data.state)) {
      if (tempLastNull == null) {
        tempLastNull = node;
      } else {
        // edges.push({
        //   id: `struct-${tempLastNull.id}-${node.id}`,
        //   source: tempLastNull.id,
        //   target: node.id,
        //   type: 'straight',
        //   style: { stroke: 'transparent', strokeWidth: 0 }, 
        // });

        tempLastNull = node;
      }
      tempProcessedStates.add(node.data.state);
    }
    else if (node.data.repo) {
      branchOrder.set(node.data.repo, []);
      branchMap.set(node.data.repo, null);
    }
  });

  nodes.forEach((node, index) => {
    var relativeOrder = []; // list of sourceTimestamp->targetTimestamp
    let { repo, state } = node.data;
    if (!state) {
      state = '';
    }

    if (index === 0) {
      // should be null node
      lastNullBranchNode = node;
      lastNodeWithNullRepo = true;
      nullBranchOrder.push(node);
      nullBranchMap.set(state, node);

      branchMap.forEach((_, branch) => {
        branchMap.set(branch, node);
        const branchOrderList = branchOrder.get(branch);
        branchOrderList.push(node);
        branchOrder.set(branch, branchOrderList);
      });
    }
    else if (repo) {
      let prevNode = branchMap.get(repo);
      const repoOrder = branchOrder.get(repo) || [];
      const firstOccurrenceNode = repoOrder.find(n => n.data.state === state);

      const temp = [];
      temp.push(prevNode.data.timestamp_utc[prevNode.data.timestamp_utc.length - 1], node.data.timestamp_utc[0]);
      if (prevNode.data.state !== node.data.state)
        relativeOrder.push(temp);

      if (firstOccurrenceNode) {
        node.data.active = false;
        const prevNodeIndex = repoOrder.indexOf(prevNode);
        const firstOccurrenceNodeIndex = repoOrder.indexOf(firstOccurrenceNode);
        firstOccurrenceNode.data.timestamp_utc.push(...node.data.timestamp_utc);

        if (prevNodeIndex < firstOccurrenceNodeIndex) {
          if (!edgeExists(prevNode.id, firstOccurrenceNode.id)) {
            edges.push({
              id: `edge-${prevNode.id}-${firstOccurrenceNode.id}`,
              source: prevNode.id,
              target: firstOccurrenceNode.id,   
              type: 'straight',
              labelStyle: { fontSize: '15px' },
              // label: calculateTimeDifference(prevNode, firstOccurrenceNode),
              style: { stroke: 'green', strokeWidth: 0.75 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: "green",
              },
            });
          }
        } else if (prevNodeIndex > firstOccurrenceNodeIndex && !edgeExists(prevNode.id, firstOccurrenceNode.id) && !categoryWiseReverseTransition.has(`${prevNode.data.category}-${firstOccurrenceNode.data.category}`)) {
          edges.push({
            id: `edge-${prevNode.id}-${firstOccurrenceNode.id}`,
            source: prevNode.id,
            target: firstOccurrenceNode.id,
            type: 'customEdge',
            // label: calculateTimeDifference(prevNode, firstOccurrenceNode),
            labelStyle: { fontSize: '15px', fill: '#00f', textAnchor: 'left', dominantBaseline: 'middle', },
            sourceHandle: 'right',
            targetHandle: 'right',
            style: { stroke: 'red', strokewidth: 0.5, strokeDasharray: '5,5', markerEnd: 'url(#arrowhead)' },
          });         
          categoryWiseReverseTransition.add(`${prevNode.data.category}-${firstOccurrenceNode.data.category}`);
        }
        branchMap.set(repo, firstOccurrenceNode);
      }
      else {
        edges.push({
          id: `edge-${prevNode.id}-${node.id}`,
          source: prevNode.id,
          target: node.id,
          type: 'straight',
          labelStyle: { fontSize: '15px' },
          style: { stroke: 'black', strokeWidth: 0.75 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 30,
            height: 30,
            color: "#b1b1b7",
          },
        });
        branchMap.set(repo, node);
        repoOrder.push(node);
        branchOrder.set(repo, repoOrder);
      }
      lastNodeWithNullRepo = false;
    } else if (repo === null) {
      // lastNodeWithNullRepo = true; // to stops rebranch merging
      let prevNode = lastNullBranchNode;
      let firstOccurrenceNode = nullBranchMap.get(state);
      const node2 = firstOccurrenceNode ? firstOccurrenceNode : node;

      if (!lastNodeWithNullRepo) {
        if(firstOccurrenceNode){
          node.data.active = false;
          firstOccurrenceNode.data.timestamp_utc.push(...node.data.timestamp_utc);
        }
        else{
          nullBranchOrder.push(node);
        }

        // eslint-disable-next-line no-unused-vars
        for (const [repo, lastBranchNode] of branchMap.entries()) {
          const temp = [];
          temp.push(lastBranchNode.data.timestamp_utc[lastBranchNode.data.timestamp_utc.length - 1], node2.data.timestamp_utc[node2.data.timestamp_utc.length - 1]);
          if (lastBranchNode.data.state !== node2.data.state)
            relativeOrder.push(temp);

          if (!edgeExists(lastBranchNode.id, node2.id)) {
            edges.push({
              id: `debranchingEdge-${lastBranchNode.id}-${node2.id}`,
              source: lastBranchNode.id,
              target: node2.id,
              type: 'straight',
              labelStyle: { fontSize: '15px' },
              // label: calculateTimeDifference(lastBranchNode, node2),
              style: { stroke: 'blue', strokewidth: 2 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 25,
                height: 25,
                color: "black",
              },
            });
          }
        }

        nullBranchMap.set(state, node2);
        lastNullBranchNode = node2;
      }
      else {
        const temp = [];
        temp.push(prevNode.data.timestamp_utc[prevNode.data.timestamp_utc.length - 1], node.data.timestamp_utc[node.data.timestamp_utc.length - 1]);
        if (prevNode.data.state !== node.data.state)
          relativeOrder.push(temp);
        
        if (firstOccurrenceNode) {
          node.data.active = false;
          const prevNodeIndex = nullBranchOrder.indexOf(prevNode);
          const firstOccurrenceNodeIndex = nullBranchOrder.indexOf(firstOccurrenceNode);
          firstOccurrenceNode.data.timestamp_utc.push(...node.data.timestamp_utc);

          if (prevNodeIndex < firstOccurrenceNodeIndex) {
            if (!edgeExists(prevNode.id, firstOccurrenceNode.id)) {
              edges.push({
                id: `edge-${prevNode.id}-${firstOccurrenceNode.id}`,
                source: prevNode.id,
                target: firstOccurrenceNode.id,
                type: 'straight',
                labelStyle: { fontSize: '15px' },
                // label: calculateTimeDifference(prevNode, firstOccurrenceNode),
                style: { stroke: 'black', strokeWidth: 0.75 },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 20,
                  height: 20,
                  color: "black",
                },
              });
            }
          }
          else if (prevNodeIndex > firstOccurrenceNodeIndex && !edgeExists(prevNode.id, firstOccurrenceNode.id) && !categoryWiseReverseTransition.has(`${prevNode.data.category}-${firstOccurrenceNode.data.category}`)) {
            edges.push({
              id: `edge-${prevNode.id}-${firstOccurrenceNode.id}`,
              source: prevNode.id,
              target: firstOccurrenceNode.id,
              type: 'customEdge',
              // label: calculateTimeDifference(prevNode, firstOccurrenceNode),
              labelStyle: { fontSize: '15px', fill: '#00f', textAnchor: 'left', dominantBaseline: 'middle', },
              sourceHandle: 'right',
              targetHandle: 'right',
              style: { stroke: 'red', strokewidth: 0.5, strokeDasharray: '5,5', markerEnd: 'url(#arrowhead)' },
            });
            categoryWiseReverseTransition.add(`${prevNode.data.category}-${firstOccurrenceNode.data.category}`);
          }
          lastNullBranchNode = firstOccurrenceNode;
        }
        else {
          if (lastNodeWithNullRepo) {
            edges.push({
              id: `edge-${prevNode.id}-${node.id}`,
              source: prevNode.id,
              target: node.id,
              type: 'straight',
              labelStyle: { fontSize: '15px' },
              // label: calculateTimeDifference(prevNode, node),
              style: { stroke: 'black', strokeWidth: 0.75 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: "#b1b1b7",
              },
            });
          }
          nullBranchMap.set(state, node);
          nullBranchOrder.push(node);
          lastNullBranchNode = node;
        }
      }

      //travel on branchOrder and get each entry push node2 to it and push it into branchorderTemp
      branchMap.forEach((_, branch) => {
        branchMap.set(branch, node2);
        const branchOrderList = branchOrder.get(branch);
        branchOrderList.push(node2);
        branchOrder.set(branch, branchOrderList);
      });
      lastNodeWithNullRepo = true;
    }

    if (relativeOrder.length > 0)
      sequence.push(relativeOrder);
  }); 
  return [edges, sequence];
};

export { createEdges, createStructure, calculateTimeDifference, createStructureOverview};
